"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type MinecraftServerState =
  | "offline"
  | "starting"
  | "online"
  | "stopping"
  | "error";

type MinecraftStatus = {
  state: MinecraftServerState;
  online: boolean;
  pid: number | null;
  startedAt: string | null;
  uptime: number | null;
  cpu: number | null;
  memory: number | null;
};

type PlayerResponse = {
  players: string[];
  count: number;
};

type ServerContextType = {
  state: MinecraftServerState;
  online: boolean;

  pid: number | null;
  startedAt: string | null;
  uptime: number | null;

  cpu: number | null;
  memory: number | null;

  players: string[];
  playerCount: number;

  loading: boolean;
  actionLoading: boolean;
  error: string | null;

  refresh: () => Promise<void>;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  restart: () => Promise<void>;
};

const ServerContext =
  createContext<ServerContextType | undefined>(
    undefined
  );

const initialStatus: MinecraftStatus = {
  state: "offline",
  online: false,

  pid: null,
  startedAt: null,
  uptime: null,

  cpu: null,
  memory: null,
};

export function ServerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [status, setStatus] =
    useState<MinecraftStatus>(
      initialStatus
    );

  const [players, setPlayers] =
    useState<string[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [actionLoading, setActionLoading] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  // -----------------------------------------------
  // Fetch status
  // -----------------------------------------------

  const fetchStatus =
    useCallback(async () => {
      const response = await fetch(
        "/api/minecraft/status",
        {
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          "Unable to retrieve server status."
        );
      }

      const data: MinecraftStatus =
        await response.json();

      setStatus(data);
    }, []);

  // -----------------------------------------------
  // Fetch players
  // -----------------------------------------------

  const fetchPlayers =
    useCallback(async () => {
      const response = await fetch(
        "/api/minecraft/players",
        {
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          "Unable to retrieve players."
        );
      }

      const data: PlayerResponse =
        await response.json();

      setPlayers(data.players);
    }, []);

  // -----------------------------------------------
  // Refresh everything
  // -----------------------------------------------

  const refresh =
    useCallback(async () => {
      try {
        await Promise.all([
          fetchStatus(),
          fetchPlayers(),
        ]);

        setError(null);
      } catch (error) {
        console.error(error);

        setError(
          "Unable to connect to the server agent."
        );
      } finally {
        setLoading(false);
      }
    }, [
      fetchStatus,
      fetchPlayers,
    ]);

  // -----------------------------------------------
  // Server actions
  // -----------------------------------------------

  async function executeAction(
    action:
      | "start"
      | "stop"
      | "restart"
  ) {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/minecraft/${action}`,
        {
          method: "POST",
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ??
            `Unable to ${action} server.`
        );
      }

      /*
       * Refresh immediately.
       *
       * Start should now give us
       * state: "starting".
       *
       * Stop should give us
       * state: "stopping".
       */
      await refresh();
    } catch (error) {
      console.error(error);

      setError(
        error instanceof Error
          ? error.message
          : "Server action failed."
      );
    } finally {
      setActionLoading(false);
    }
  }

  async function start() {
    await executeAction("start");
  }

  async function stop() {
    await executeAction("stop");
  }

  async function restart() {
    await executeAction(
      "restart"
    );
  }

  // -----------------------------------------------
  // Polling
  // -----------------------------------------------

  useEffect(() => {
    refresh();

    /*
     * 2 seconds feels much better now
     * that we're showing startup state,
     * CPU and RAM.
     */
    const interval =
      window.setInterval(
        refresh,
        2000
      );

    return () => {
      window.clearInterval(
        interval
      );
    };
  }, [refresh]);

  // -----------------------------------------------
  // Provider
  // -----------------------------------------------

  return (
    <ServerContext.Provider
      value={{
        state: status.state,
        online: status.online,

        pid: status.pid,
        startedAt:
          status.startedAt,
        uptime: status.uptime,

        cpu: status.cpu,
        memory: status.memory,

        players,
        playerCount:
          players.length,

        loading,
        actionLoading,
        error,

        refresh,
        start,
        stop,
        restart,
      }}
    >
      {children}
    </ServerContext.Provider>
  );
}

export function useServer() {
  const context =
    useContext(ServerContext);

  if (!context) {
    throw new Error(
      "useServer must be used inside ServerProvider"
    );
  }

  return context;
}